import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Button, Tooltip, Grid, TextField, MenuItem } from '@mui/material';
import styles from './styles.module.css';
import DataTable from 'components/utils/DataTable/';
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import SearchIcon from '@mui/icons-material/Search';
import PurchaseService from 'services/PurchaseService';
import { Link, useHistory } from 'react-router-dom';
import ListDialog from 'components/popups/ListDialog';
import ConfirmationDialog from 'components/popups/ConfirmationDialog';
import ConfirmPopup from 'components/utils/Alert/ConfirmPopup';
import Info from 'components/utils/Alert/Info';
import RestrictionInfo from 'components/utils/Alert/RestrictionInfo';
import withConsoleBase from 'components/utils/ConsoleBase/withConsoleBase';
import { ERROR_CODES, ROUTES, SUBSCRIPTION_TYPE, USER_PERMISSIONS } from 'const';
import { ShopContext } from 'Context/ShopContext';
import { UserContext } from 'Context/UserContext';
import { InventoryContext } from 'Context/InventoryContext';
import DateTimeHelpers from 'helpers/DateTimeHelpers';
import { AlertMessagesContext } from 'react-alert-messages';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import { saveAs } from 'file-saver';
import CsvHelper from 'helpers/CsvHelper';
import SyncIcon from '@mui/icons-material/Sync';
import ShopsHelper from 'helpers/ShopsHelper';
import Grayout from 'components/utils/GrayOut/Grayout';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ReplayRoundedIcon from '@mui/icons-material/ReplayRounded';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import DateTimeRange from 'components/utils/DateTimeRange/DateTimeRange';
import { dynamicDateRange } from 'services/DynamicDateRangeService';
import BarcodePrintDialog from 'components/popups/BarcodePrintDialog';
import QrCodeIcon from '@mui/icons-material/QrCode';
import { useTranslation } from 'react-i18next';
import { WarehouseContext } from 'Context/WarehouseContext';
import { featureIsExpired } from 'components/utils/FeatureValidityExpire';

function PurchaseHistory() {
  const { t: tCommon } = useTranslation('common');
  const { t: tOperations } = useTranslation('operations');
  const { shop } = useContext(ShopContext);
  const { user } = useContext(UserContext);
  const { getProducts, getStockBatches } = useContext(InventoryContext);
  const { getWarehouseProducts } = useContext(WarehouseContext);
  const { postAlertMessage } = useContext(AlertMessagesContext);

  const [filterFromDate, setFilterFromDate] = useState(new Date());
  const [filterToDate, setFilterToDate] = useState(new Date());

  const [searchText, setSearchText] = useState('');
  const [purchases, setPurchase] = useState('');
  const [viewItems, setViewItems] = useState([]);
  const [listTitle, setListTitle] = useState('');
  const [loadingIndicator, setLoadingIndicator] = useState(false);
  const [deleteData, setDeleteData] = useState(false);
  const [isShowGenerateButton, setIsShowGenerateButton] = useState(null);
  const [sorting, setSorting] = useState('purchase_date_desc');
  const [prevFilterData, setPrevFilterData] = useState({
    filterFromDate,
    filterToDate,
    sorting: 'purchase_date_desc',
  });
  const [printProductBarcode, setPrintProductBarcode] = useState(null);
  const [isShowForceDeleteDialog, setShowForceDeleteDialog] = useState(false);
  const [pendingDeleteRow, setPendingDeleteRow] = useState(null);

  const subscriptionType = shop?.subscription?.type;
  const history = useHistory();
  const userHasPurchaseWritePermission = user?.shop_permissions?.purchase_permission === USER_PERMISSIONS.WRITE;

  const CSV_COLUMNS = {
    id: tCommon('ID'),
    invoice_no: tCommon('Invoice Number'),
    bill_date: tCommon('Bill Date'),
    date: tCommon('Date'),
    vendor: {
      title: tCommon('Vendor Name'),
      getValue: (purchaseLineDetails) => purchaseLineDetails?.vendor?.name ?? purchaseLineDetails?.vendor_name_old ?? '-',
    },
    plu: {
      title: tCommon('PLU'),
      getValue: (purchaseLineDetails) => purchaseLineDetails?.purchaseItem?.plu || 0,
    },
    name: {
      title: tCommon('Item Name'),
      getValue: (purchaseLineDetail) => purchaseLineDetail?.purchaseItem?.item?.name || '',
    },
    rate: {
      title: tCommon('Cost'),
      getValue: (purchaseLineDetail) => purchaseLineDetail?.purchaseItem?.rate || 0,
    },
    quantity: {
      title: tCommon('Quantity'),
      getValue: (purchaseLineDetail) => purchaseLineDetail?.purchaseItem?.quantity || 0,
    },
    tax: {
      title: tCommon('Tax'),
      getValue: (purchaseLineDetail) => {
        return ShopsHelper.getAmountFormatted(shop, purchaseLineDetail?.purchaseItem?.tax || 0);
      },
    },
    total: {
      title: tCommon('Total'),
      getValue: (purchaseLineDetail) => {
        const amount =
          (purchaseLineDetail?.purchaseItem?.rate || 0) * (purchaseLineDetail?.purchaseItem?.quantity || 0) +
          (purchaseLineDetail?.purchaseItem?.tax || 0);
        return ShopsHelper.getAmountFormatted(shop, amount);
      },
    },
    total_amount: {
      title: tCommon('Purchase Total'),
      getValue: (purchaseLineDetail) => {
        return ShopsHelper.getAmountFormatted(shop, purchaseLineDetail?.total_amount || 0);
      },
    },
  };

  const ONE_PURCHASE_CSV_COLUMNS = {
    purchase: tCommon('Purchase ID'),
    id: tCommon('Purchase item ID'),
    plu: tCommon('PLU'),
    name: tCommon('Item Name'),
    rate: tCommon('Rate'),
    price: tCommon('Unit Price'),
    stock: tCommon('Stock'),
    quantity: tCommon('Quantity'),
    tax: tCommon('Tax'),
    total: tCommon('Total'),
  };

  const handleEditPurchase = (row) => {
    history.push(ROUTES.EDIT_PURCHASE.replace(':id', row.id));
  };
  const subTotal = (items) => {
    return items.reduce(function (a, b) {
      return a + b['total'];
    }, 0);
  };

  const handleItemsView = (row = '') => {
    if (row) {
      const selectedItems = row.items;
      setListTitle(
        `${tOperations('Purchased items from')} ${row?.vendor?.name ?? row.vendor_name_old} ${tOperations('on')} ${
          row.date
        }`
      );
      let data = selectedItems.map((item) => ({
        ...item,
        name: item?.item?.name,
        price: item?.item?.price,
        profit_margin: item?.item?.config?.profit_margin ? `${item.item?.config?.profit_margin}%` : '0%',
        stock: item?.item?.stock,
        total: Number(item.quantity) * Number(item.rate) + Number(item.tax),
      }));

      const grandTotal = subTotal(data);

      data = data.map((item) => ({
        ...item,
        total: ShopsHelper.getAmountFormatted(shop, item.total),
      }));

      data = [
        ...data,
        {
          plu: '',
          name: '',
          rate: '',
          profit_margin: '',
          price: '',
          stock: '',
          quantity: '',
          tax: tCommon('Total'),
          total: ShopsHelper.getAmountFormatted(shop, grandTotal),
        },
      ];
      return setViewItems(data);
    }
    return setViewItems([]);
  };
  const handleDeleteClick = (data) => setDeleteData(data);
  const handlePurchaseDeletion = async (row, forceUpdate = false) => {
    const id = row.id;
    try {
      const data = forceUpdate ? { force_update: true } : '';
      await PurchaseService.deletePurchase(id, data);
      await getPurchase();

      const isPremium = shop?.subscription?.type === SUBSCRIPTION_TYPE.PREMIUM;
      const hasBatchEnabled = (row.items || []).some((pi) => pi.item?.is_batch_enabled);
      const isWarehouseEnabled = !featureIsExpired(shop?.warehouse_validity);

      const promises = [getProducts({ useCache: false })];
      if (isPremium && hasBatchEnabled) {
        promises.push(getStockBatches({ useCache: false }));
      }
      if (isWarehouseEnabled) {
        promises.push(getWarehouseProducts({ useCache: false }));
      }
      await Promise.all(promises);

      postAlertMessage({
        text: tCommon('Purchase deleted successfully'),
        type: 'success',
      });
    } catch (err) {
      if (err.status_code === ERROR_CODES.BATCH_RECORDS_EXIST) {
        setPendingDeleteRow(row);
        setShowForceDeleteDialog(true);
      } else {
        postAlertMessage({ text: err.message, type: 'failed' });
      }
    } finally {
      setDeleteData(false);
    }
  };

  const handleConfirmForceDelete = async () => {
    setShowForceDeleteDialog(false);
    if (pendingDeleteRow) {
      await handlePurchaseDeletion(pendingDeleteRow, true);
    }
  };

  const toCsvBtnPressed = async () => {
    const csvFormattedPurchases = purchases.flatMap((obj) =>
      obj.items.map((purchaseItem, index) => ({
        id: obj.id,
        invoice_no: obj.invoice_no,
        bill_date: obj.bill_date,
        date: obj.date,
        vendor: obj.vendor,
        vendor_name_old: obj.vendor_name_old,
        total_amount: index === 0 ? obj.total_amount : undefined,
        purchaseItem: {
          ...purchaseItem,
          name: purchaseItem.item.name,
        },
      }))
    );

    console.log('csvFormattedPurchases', csvFormattedPurchases);

    const csv = CsvHelper.getString(csvFormattedPurchases, CSV_COLUMNS);
    const blob = new Blob([csv], {
      type: 'text/csv',
    });
    const dateTime = DateTimeHelpers.getDate(new Date());
    saveAs(blob, `PURCHASES-${dateTime}.csv`);

    postAlertMessage({
      text: tCommon('Exported to excel successfully'),
      type: 'success',
    });
  };

  const _getPurchasesFiltered = (purchases, searchKey) => {
    if (!searchKey) {
      return purchases;
    }

    const searchKeySmallCase = searchKey.toLowerCase();
    return (purchases || []).filter(
      (purchase) =>
        (purchase.vendor?.name ?? '').toLowerCase().includes(searchKeySmallCase) ||
        (purchase?.vendor_name_old ?? '').toLowerCase().includes(searchKeySmallCase) ||
        purchase.date.includes(searchKeySmallCase) ||
        purchase.total_amount.toString().includes(searchKeySmallCase)
    );
  };

  const _getPurchasesAmountSum = (purchases) => {
    if (!purchases || !purchases.length) {
      return 0;
    }

    let sum = 0;
    purchases.forEach((purchase) => (sum += purchase.total_amount));
    return ShopsHelper.getAmountFormatted(shop, sum);
  };

  const handleReturnPurchaseClick = (purchase) => {
    history.push(`${ROUTES.NEW_PURCHASE_RETURN}?purchase_id=${purchase.id}`);
  };

  const handleCloseDetailsDialog = () => {
    setViewItems(null);
  };

  const headerData = [
    {
      label: tCommon('Invoice Number'),
      id: 'invoice_no',
      type: 'text',
    },
    {
      label: tCommon('Vendor Name'),
      id: 'vendor',
      type: 'callback',
      viewRender: (item) => item?.vendor?.name ?? item?.vendor_name_old ?? '-',
    },
    {
      label: tCommon('Purchase Date'),
      id: 'date',
      type: 'callback',
      viewRender: (item) => `${DateTimeHelpers.formatDMYTo12Hour(item.date)}`,
    },
    {
      label: tCommon('Total Amount'),
      id: 'total_amount',
      type: 'floatAmount',
    },
    {
      label: tCommon('Actions'),
      id: 'actions',
      type: 'callback',
      viewRender: (item) => {
        return (
          <Grid className={styles.btnGroup}>
            <Tooltip title={tOperations('View Details')} onClick={() => handleItemsView(item)}>
              <RemoveRedEyeIcon className={styles.iconButton} />
            </Tooltip>
            {userHasPurchaseWritePermission && (
              <>
                <Tooltip
                  title={tOperations('Print Barcode')}
                  onClick={() => {
                    setPrintProductBarcode(item);
                  }}
                >
                  <QrCodeIcon className={styles.iconButton} />
                </Tooltip>
                <Tooltip title={tOperations('Edit Purchase')} onClick={() => handleEditPurchase(item)}>
                  <EditIcon className={styles.iconButton} />
                </Tooltip>
                <Tooltip title={tOperations('Delete Purchase')} onClick={() => handleDeleteClick(item)}>
                  <DeleteIcon className={styles.iconButton} />
                </Tooltip>
                <Tooltip title={tOperations('Purchase Return')} onClick={() => handleReturnPurchaseClick(item)}>
                  <ReplayRoundedIcon className={styles.iconButton} />
                </Tooltip>
              </>
            )}
          </Grid>
        );
      },
    },
  ];

  const listHeaderData = [
    {
      label: tCommon('PLU'),
      id: 'plu',
      type: 'text',
    },
    {
      label: tCommon('Item Name'),
      id: 'name',
      type: 'text',
    },
    {
      label: tCommon('Rate'),
      id: 'rate',
      type: 'text',
    },
    {
      label: tCommon('Margin (%)'),
      id: 'profit_margin',
      type: 'text',
    },
    {
      label: tCommon('Unit Price'),
      id: 'price',
      type: 'text',
    },
    {
      label: tCommon('Stock'),
      id: 'stock',
      type: 'text',
    },
    {
      label: tCommon('Quantity'),
      id: 'quantity',
      type: 'text',
    },
    {
      label: tCommon('Tax'),
      id: 'tax',
      type: 'text',
    },
    {
      label: tCommon('Total'),
      id: 'total',
      type: 'text',
    },
  ];

  const _getLoadPurchaseRequestParams = useCallback(() => {
    const { startDate: dynamicStartDate, endDate: dynamicEndDate } = dynamicDateRange('PURCHASE_HISTORY');
    return {
      date_start: DateTimeHelpers.getDateYMD(dynamicStartDate),
      date_end: DateTimeHelpers.getDateYMD(dynamicEndDate),
      sort: sorting,
    };
  }, [sorting]);

  const getPurchase = useCallback(async () => {
    setLoadingIndicator(true);
    try {
      const params = _getLoadPurchaseRequestParams();
      const res = await PurchaseService.getPurchaseDetails(params);
      setPurchase(res);
    } catch (err) {
      postAlertMessage({ text: err.message, type: 'failed' });
    }
    setLoadingIndicator(false);
  }, [_getLoadPurchaseRequestParams, postAlertMessage]);

  const generatePurchaseHistory = () => {
    const { startDate: dynamicStartDate, endDate: dynamicEndDate } = dynamicDateRange('PURCHASE_HISTORY');

    setFilterFromDate(dynamicStartDate);
    setFilterToDate(dynamicEndDate);
    setSorting(sorting);

    getPurchase();
    setIsShowGenerateButton(false);
    setPrevFilterData({
      filterFromDate: dynamicStartDate,
      filterToDate: dynamicEndDate,
      sorting,
    });
  };

  const isAnyChangeOnReportsFilters = useCallback(() => {
    return (
      filterFromDate !== prevFilterData.filterFromDate ||
      filterToDate !== prevFilterData.filterToDate ||
      sorting !== prevFilterData.sorting
    );
  }, [
    filterFromDate,
    filterToDate,
    prevFilterData.filterFromDate,
    prevFilterData.filterToDate,
    sorting,
    prevFilterData.sorting,
  ]);

  useEffect(() => {
    generatePurchaseHistory();
    //eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (isShowGenerateButton === null) return;
    setIsShowGenerateButton(isAnyChangeOnReportsFilters());
  }, [isAnyChangeOnReportsFilters, isShowGenerateButton]);

  const purchasesToDisplay = _getPurchasesFiltered(purchases, searchText);
  const purchaseAmountSum = _getPurchasesAmountSum(purchases);

  return (
    <div className={styles.contentWrapper}>
      <ConfirmPopup
        data={deleteData}
        handleClose={handleDeleteClick}
        handleOkay={handlePurchaseDeletion}
        alertTitle={tOperations('Confirm Delete')}
        successBtnName={tOperations('Delete')}
        alertContent={tOperations(
          "Deleted Purchase History can't be restored, do you want to delete the Purchase History?"
        )}
      />
      <div className={styles.titleSec}>
        <h2 className={styles.title}>
          {tCommon('Purchase')} <span className={styles.menuTitle}>{tCommon('Management')}</span>
        </h2>
        {subscriptionType && subscriptionType !== SUBSCRIPTION_TYPE.BASIC && (
          <div style={{ justifyContent: 'flex-end' }}>
            <div style={{ paddingBottom: '4px' }}>
              <label className={styles.label}>{tOperations('Export As')}</label>
            </div>
            <Button
              variant="contained"
              color="primary"
              className={styles.actionBtn}
              style={{ backgroundColor: '#00a65a', marginRight: 5 }}
              onClick={toCsvBtnPressed}
            >
              <ImportExportIcon className={styles.actionBtnIcon} />
              {tOperations('CSV')}
            </Button>
          </div>
        )}
      </div>
      {subscriptionType && subscriptionType === SUBSCRIPTION_TYPE.BASIC && (
        <RestrictionInfo
          title={tOperations('Feature not available')}
          content={tOperations('To get purchase history options upgrade subscription to premium')}
        />
      )}
      {subscriptionType && subscriptionType !== SUBSCRIPTION_TYPE.BASIC && (
        <>
          <div className={styles.changeable}>
            <div className={styles.filterSec}>
              <div className={styles.headTitle}>
                <h2 className={styles.subTitle}>
                  {tOperations('Purchase History for')} {DateTimeHelpers.convertDateToDMY(filterFromDate)}{' '}
                  {tCommon('To')} {DateTimeHelpers.convertDateToDMY(filterToDate)}
                </h2>
              </div>
              <div className={styles.filerInputSec}>
                {userHasPurchaseWritePermission && (
                  <Link to={ROUTES.NEW_PURCHASE} style={{ textDecorationColor: 'transparent' }}>
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      style={{ backgroundColor: '#ff851b' }}
                      className={styles.actionBtn}
                    >
                      <AddShoppingCartIcon className={styles.actionBtnIcon} />
                      {tOperations('New Purchase')}
                    </Button>
                  </Link>
                )}
                <div className={styles.searchSec}>
                  <input
                    type="text"
                    value={searchText}
                    onChange={(e) => {
                      setSearchText(e.target.value);
                    }}
                    className={styles.searchInput}
                    placeholder={tOperations('Search purchase')}
                  />
                  <Tooltip title={tCommon('Search')} placement="bottom">
                    <SearchIcon className={styles.searchIcon} />
                  </Tooltip>
                </div>
              </div>
            </div>
            <div className={styles.actionButtons}>
              <div className={styles.filterDiv}>
                <DateTimeRange
                  setFilterFromDate={setFilterFromDate}
                  setFilterToDate={setFilterToDate}
                  storageKey="PURCHASE_HISTORY"
                />
              </div>
              <div className={styles.filterDiv}>
                <div style={{ paddingBottom: '4px' }}>
                  <label className={styles.label}>{tOperations('Sorting')}</label>
                </div>
                <div>
                  <TextField
                    select
                    size="small"
                    variant="outlined"
                    color="primary"
                    style={{ width: 135 }}
                    value={sorting}
                    onChange={(event) => {
                      setSorting(event.target.value);
                    }}
                  >
                    <MenuItem value={'purchase_date_asc'}>{tCommon('Purchase Date (↑)')}</MenuItem>
                    <MenuItem value={'purchase_date_desc'}>{tCommon('Purchase Date (↓)')}</MenuItem>
                    <MenuItem value={'bill_date_asc'}>{tCommon('Bill Date (↑)')}</MenuItem>
                    <MenuItem value={'bill_date_desc'}>{tCommon('Bill Date (↓)')}</MenuItem>
                  </TextField>
                </div>
              </div>
              <div className={styles.filterDiv}>
                <div style={{ paddingBottom: '4px' }}>
                  <label className={styles.label}>{tOperations('Load Purchases')}</label>
                </div>
                <div>
                  <Button
                    variant="contained"
                    color="primary"
                    className={styles.actionBtn}
                    style={{ backgroundColor: '#00a65a' }}
                    onClick={generatePurchaseHistory}
                    disabled={isShowGenerateButton === false}
                  >
                    <SyncIcon className={styles.actionBtnIcon} />
                    {tOperations('Generate')}
                  </Button>
                </div>
              </div>
            </div>
          </div>
          {isShowGenerateButton === null ? (
            <Info
              title={tOperations('Set filters and click load button')}
              content={tOperations(
                'Purchase history are generated based on the filters. Please set filters and click load purchase button to get purchase history.'
              )}
            />
          ) : (
            (purchases || loadingIndicator) &&
            ((purchasesToDisplay && purchasesToDisplay.length) || loadingIndicator ? (
              <div className={styles.tableContainer}>
                <Grayout open={isShowGenerateButton} />
                <DataTable
                  columns={headerData}
                  rows={purchasesToDisplay || []}
                  isLoading={loadingIndicator}
                  summary={{ date: 'Total', total_amount: purchaseAmountSum }}
                />
              </div>
            ) : (
              !loadingIndicator && (
                <Info
                  title={tOperations('You have no purchases to list')}
                  content={tOperations("You haven't any purchase to list with given properties")}
                />
              )
            ))
          )}

          {viewItems && viewItems.length > 0 && (
            <ListDialog
              onClose={handleCloseDetailsDialog}
              toggleItemsTab={handleItemsView}
              tableHeader={listHeaderData}
              rowData={viewItems}
              title={listTitle}
              emptyDataTitle={tOperations('No purchase items')}
              emptyDataContent=""
              csvColumns={ONE_PURCHASE_CSV_COLUMNS}
            />
          )}
          {printProductBarcode && (
            <BarcodePrintDialog
              handleClose={() => {
                setPrintProductBarcode(null);
              }}
              items={printProductBarcode.items.map(({ item, ...rest }) => ({ ...rest, ...item }))}
            />
          )}
          <ConfirmationDialog
            open={isShowForceDeleteDialog}
            handleClose={() => setShowForceDeleteDialog(false)}
            handleConfirm={handleConfirmForceDelete}
            title={tOperations('Confirm Force Delete')}
            message={tOperations(
              'Inventory Warning: Sales already exist for items in this batch. Deleting this purchase requires a force update to maintain consistency. Do you wish to continue?'
            )}
            confirmText={tCommon('Yes')}
            cancelText={tCommon('Cancel')}
            loading={loadingIndicator}
          />
        </>
      )}
    </div>
  );
}

export default withConsoleBase(PurchaseHistory);
